// src/pages/TraceabilityTimeline.jsx

import React, { useState, useEffect, useRef } from 'react';
import { Wrench, User, Info } from 'lucide-react';

const BRAND_BLUE = '#326BDF';

const LOCATION_ICONS = {
  manufacturing: '/manufacturer.png',
  distributor:   '/distributor.png',
  retailer:      '/retailer.png',
  recycler:      '/warehouse.png',
  repairer:      null,
  enduser:       null,
};

const LOCATION_LABELS = {
  manufacturing: 'Manufacturing',
  distributor:   'Distributor',
  retailer:      'Retailer',
  recycler:      'Recycler',
  repairer:      'Repair/Refurb',
  enduser:       'End User',
};

const BLUE_FILTER = 'brightness(0) saturate(100%) invert(27%) sepia(97%) saturate(600%) hue-rotate(204deg) brightness(100%)';
const GREY_FILTER = 'brightness(0) opacity(0.3)';

const STANDARD_ORDER = ['Manufacturing', 'Distributor', 'Retailer', 'EndUser', 'Repairer', 'Recycler'];

function getLabel(locationType) {
  return LOCATION_LABELS[locationType?.toLowerCase()] || locationType;
}

function formatTimestamp(ts) {
  if (!ts) return null;
  try {
    return new Date(ts).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return ts; }
}

// ── Passport detail card — matches FieldBox styling exactly ──────────────────
function DetailCard({ label, value, isLink }) {
  return (
    <div className="p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-100" style={{ flex: '1 1 160px', minWidth: 140 }}>
      <div className="flex items-center gap-2 mb-2">
        <Info className="w-4 h-4 text-blue-600 flex-shrink-0" />
        <span className="text-xs font-medium text-gray-600">{label}</span>
      </div>
      {isLink && value ? (
        <a
          href={value.startsWith('http') ? value : `https://${value}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-600 font-medium break-all underline"
        >
          {value}
        </a>
      ) : (
        <p className="text-sm text-gray-800 font-medium break-words">{value || '—'}</p>
      )}
    </div>
  );
}

// ── Single node ──────────────────────────────────────────────────────────────
function JourneyNode({ locationType, isVisited, isCurrent, journeyEntry }) {
  const key     = locationType.toLowerCase();
  const iconSrc = LOCATION_ICONS[key];
  const label   = getLabel(locationType);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, minWidth: 120 }}>
      <p style={{ fontSize: 12, fontWeight: 600, textAlign: 'center', margin: 0, color: isVisited ? BRAND_BLUE : '#9CA3AF' }}>
        {label}
      </p>
      <div style={{
        width: 64, height: 64, borderRadius: '50%',
        backgroundColor: isVisited ? '#EAF0FB' : '#F3F4F6',
        border: `2px solid ${isVisited ? BRAND_BLUE : '#E5E7EB'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
        animation: isCurrent ? 'iconPulse 3s ease-in-out infinite' : 'none',
      }}>
        {iconSrc ? (
          <img src={iconSrc} alt={label} style={{ width: 36, height: 36, objectFit: 'contain', filter: isVisited ? BLUE_FILTER : GREY_FILTER }} />
        ) : (
          <div style={{ filter: isVisited ? BLUE_FILTER : GREY_FILTER }}>
            {key === 'repairer' && <Wrench size={32} color="#000" strokeWidth={1.5} />}
            {key === 'enduser'  && <User   size={32} color="#000" strokeWidth={1.5} />}
          </div>
        )}
      </div>

      {isVisited && journeyEntry && (
        <div style={{ textAlign: 'center', paddingLeft: 4, paddingRight: 4 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#111827', margin: 0 }}>{journeyEntry.location_id}</p>
          {journeyEntry.from && (
            <p style={{ fontSize: 10, color: '#6B7280', margin: '2px 0 0' }}>In: {formatTimestamp(journeyEntry.from)}</p>
          )}
          {journeyEntry.to ? (
            <p style={{ fontSize: 10, color: '#9CA3AF', margin: '1px 0 0' }}>Out: {formatTimestamp(journeyEntry.to)}</p>
          ) : (
            <p style={{ fontSize: 10, fontWeight: 600, color: BRAND_BLUE, margin: '1px 0 0' }}>Currently here</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Connector ────────────────────────────────────────────────────────────────
function JourneyConnector({ isVisited }) {
  return (
    <div style={{
      minWidth: 48, marginTop: 40, alignSelf: 'flex-start',
      display: 'flex', alignItems: 'center',
      flexShrink: 0, marginLeft: 4, marginRight: 4,
    }}>
      <div style={{ flex: 1, borderTop: `2px ${isVisited ? 'solid' : 'dashed'} ${isVisited ? BRAND_BLUE : '#D1D5DB'}` }} />
      <div style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: isVisited ? BRAND_BLUE : '#D1D5DB', flexShrink: 0, marginLeft: 3 }} />
    </div>
  );
}

// ── Transactional category keys for RBAC ─────────────────────────────────────
const TRANSACTIONAL_KEYS = {
  dpp_created_by:               'dpp_created_by',
  data_carrier_type:            'data_carrier_type',
  link_to_digital_copy:         'link_to_digital_copy',
  ownership:                    'ownership',
  passport_availability_period: 'passport_availability_period',
};

// ── Main ─────────────────────────────────────────────────────────────────────
function TraceabilityTimeline({ dppId, transactionalData, visibleFields }) {
  const canShow = (key) => !visibleFields || visibleFields.includes(key);
  const [journey, setJourney] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const scrollRef             = useRef(null);
  const [scrollState, setScrollState] = useState({ left: 0, width: 0, client: 0 });

  useEffect(() => {
    if (!dppId) return;
    const fetchJourney = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('access_token') || localStorage.getItem('authToken');
        const res = await fetch(
          `https://dpp-api.acespireconsulting.com/api/product-journey/${dppId}`,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        if (!res.ok) throw new Error(`Server error: ${res.status}`);
        const data = await res.json();
        setJourney(data.journey || []);
      } catch (err) {
        setError(err.message || 'Failed to load journey');
      } finally {
        setLoading(false);
      }
    };
    fetchJourney();
  }, [dppId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => setScrollState({ left: el.scrollLeft, width: el.scrollWidth, client: el.clientWidth });
    update();
    el.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => { el.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
  }, [journey]);

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 0', gap: 12 }}>
      <div className="w-7 h-7 border-4 border-gray-200 border-t-[#326BDF] rounded-full animate-spin" />
      <p style={{ fontSize: 14, color: '#9CA3AF', margin: 0 }}>Loading journey...</p>
    </div>
  );

  if (error) return (
    <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '12px 16px', fontSize: 14, color: '#B91C1C' }}>{error}</div>
  );

  // ── ONLY CHANGE: iterate journey array directly instead of deduplicating ──
  // Removed: visitedMap, visitedTypes, visitedOrdered, unvisited, displayPath
  // Every element in journey[] renders as its own node — repeated types show separately

  const currentEntry = [...journey].reverse().find(e => e.to === null);
  const currentType  = currentEntry?.location_type || null;

  const needsScroll = scrollState.width > scrollState.client + 1;
  const thumbWidth  = needsScroll ? Math.max(40, (scrollState.client / scrollState.width) * scrollState.client) : 0;
  const thumbLeft   = needsScroll
    ? (scrollState.left / (scrollState.width - scrollState.client)) * (scrollState.client - thumbWidth)
    : 0;

  const handleThumbDrag = (e) => {
    e.preventDefault();
    const startX = e.clientX, startLeft = scrollState.left, track = scrollState.client - thumbWidth;
    const onMove = (me) => { if (scrollRef.current) scrollRef.current.scrollLeft = startLeft + ((me.clientX - startX) / track) * (scrollState.width - scrollState.client); };
    const onUp   = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const formatTs = (ts) => {
    if (!ts) return '—';
    try { return new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return ts; }
  };

  return (
    <>
      <style>{`
        @keyframes iconPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.07); } }
        .journey-scroll::-webkit-scrollbar { display: none; }
        .journey-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* ── Passport cards at top — only when transactionalData passed ── */}
        {transactionalData && (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <DetailCard label="DPP ID"               value={transactionalData.id || dppId} />
              <DetailCard label="Created At"           value={formatTs(transactionalData.created_at)} />
              {canShow('dpp_created_by') && (
                <DetailCard label="Created By"         value={transactionalData.dpp_created_by} />
              )}
              {canShow('data_carrier_type') && (
                <DetailCard label="Data Carrier"       value={transactionalData.data_carrier_type} />
              )}
              {canShow('ownership') && (
                <DetailCard label="Ownership"          value={transactionalData.ownership} />
              )}
              {canShow('passport_availability_period') && (
                <DetailCard label="Availability Period" value={transactionalData.passport_availability_period} />
              )}
              {canShow('link_to_digital_copy') && (
                <DetailCard label="Link to Digital Copy" value={transactionalData.link_to_digital_copy} isLink />
              )}
            </div>
            <div style={{ borderTop: '1px solid #DBEAFE' }} />
          </>
        )}

        {/* ── Timeline ── */}
        {journey.length === 0 ? (
          <p style={{ fontSize: 14, color: '#9CA3AF', textAlign: 'center', margin: '32px 0' }}>
            Journey not yet started for this product.
          </p>
        ) : (
          <>
            <div ref={scrollRef} className="journey-scroll" style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
              <div style={{ display: 'inline-flex', alignItems: 'flex-start', minWidth: 'max-content', paddingBottom: 4 }}>
                {journey.map((stop, index) => {
                  const isCurrent = stop.location_type === currentType && stop.to === null;
                  const isLast    = index === journey.length - 1;

                  return (
                    <React.Fragment key={`${stop.location_id}-${index}`}>
                      <JourneyNode
                        locationType={stop.location_type}
                        isVisited={true}
                        isCurrent={isCurrent}
                        journeyEntry={stop}
                      />
                      {!isLast && <JourneyConnector isVisited={true} />}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* Custom scrollbar */}
            {needsScroll && (
              <div
                style={{ width: '100%', height: 5, backgroundColor: '#E5E7EB', borderRadius: 4, position: 'relative', cursor: 'pointer' }}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  if (scrollRef.current) scrollRef.current.scrollLeft = ((e.clientX - rect.left) / scrollState.client) * (scrollState.width - scrollState.client);
                }}
              >
                <div
                  style={{ position: 'absolute', top: 0, left: thumbLeft, width: thumbWidth, height: 5, backgroundColor: BRAND_BLUE, borderRadius: 4, cursor: 'grab' }}
                  onMouseDown={handleThumbDrag}
                />
              </div>
            )}

            {/* Status */}
            <p style={{ fontSize: 14, fontWeight: 500, color: '#4B5563', margin: 0 }}>
              {currentType
                ? `Product is currently at ${getLabel(currentType)} · ${currentEntry.location_id}`
                : 'Journey completed'
              }
            </p>
          </>
        )}
      </div>
    </>
  );
}

export default TraceabilityTimeline;